import { FC, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { Alert, Snackbar, StrongModalText } from '@atoms';
import { PERMISSIONS } from '@globalConstants';
import { usePermissions, useRoles, useURLparams } from '@hooks';
import { FullWidthContent, MainMenuLayout } from '@layouts';
import { Table } from '@molecules';
import { DEFAULT_ROWS_PER_PAGE, pendingCustomersColumns } from './constants';
import useGetPendingCustomers, {
  PendingCustomer,
  PendingCustomerStatus,
} from './data/useGetPendingCustomers';
import useResolvePendingCustomer from './data/useResolvePendingCustomer/hooks/useResolvePendingCustomer';
import SearchBoxPendingCustomers from './searchBox';
import { PendingCustomersSearchFormValues } from './searchBox/constants';

interface PendingCustomersParams {
  page: string;
  limit: string;
  status?: PendingCustomerStatus | '';
}

/**
 * Screen to approve or reject pending customer registrations
 */
const PendingCustomersScreen: FC = () => {
  const navigate = useNavigate();
  const { validateRoles } = useRoles();
  const canApproveClient = usePermissions([PERMISSIONS.approveClient]);
  const { params, setURLparams } = useURLparams<PendingCustomersParams>({
    page: '1',
    limit: String(DEFAULT_ROWS_PER_PAGE),
  });

  const rowsPerPage = Number(params?.limit || DEFAULT_ROWS_PER_PAGE);

  const {
    data: response,
    isLoading,
    handlePageChange,
    handleRowsPerPageChange,
    handleRefresh,
  } = useGetPendingCustomers({
    params,
    setURLparams,
  });

  const {
    handleSubmit,
    onActionClick,
    isModalOpen,
    selectedCustomer,
    setIsModalOpen,
    isError,
    setIsError,
    setIsSuccess,
    isSuccess,
    alertMessage,
    isLoading: isLoadingAction,
  } = useResolvePendingCustomer(() => {
    handleRefresh();
  });

  const columns = useMemo(
    () => pendingCustomersColumns.filter((column) => !column.role || validateRoles(column.role)),
    [validateRoles]
  );

  const items = useMemo(() => {
    return (response?.page || []).map((item) => ({
      customer: item,
      callback: onActionClick,
    }));
  }, [response?.page, onActionClick]);

  const filters: PendingCustomersSearchFormValues = {
    status: params?.status || '',
  };

  /**
   * Applies status filter and resets to first page
   */
  const handleSearch = (nextFilters: PendingCustomersSearchFormValues) => {
    const nextParams: Record<string, string> = {
      ...params,
      page: '1',
      limit: String(rowsPerPage),
    };

    if (nextFilters.status) {
      nextParams.status = nextFilters.status;
    } else {
      delete nextParams.status;
    }

    setURLparams(nextParams);
  };

  /**
   * Navigates to pending customer detail screen
   * @param item - Table row item
   */
  const navigateToDetail = (item: { customer: PendingCustomer }) => {
    navigate(`/altas/gestion-razones-sociales/${item.customer.id}`);
  };

  return (
    <MainMenuLayout>
      <FullWidthContent>
        <SearchBoxPendingCustomers filters={filters} onSearch={handleSearch} />
        <Table
          columns={columns}
          count={response?.totalCount || 0}
          isLoading={isLoading}
          items={items}
          page={params?.page || 1}
          rowsPerPage={params?.limit || DEFAULT_ROWS_PER_PAGE}
          //TODO: Cast the typing to avoid build errors.
          onPageChange={handlePageChange as () => void}
          onRowsPerPageChange={handleRowsPerPageChange as () => void}
          onItemClick={navigateToDetail}
        />
        <Alert
          variant='warning'
          modalVisible={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
          handleSubmit={handleSubmit}
          closeButtonText='COMMON:CANCEL'
          title={t('PROSPECTS:SEND_EMAIL:MODAL:TITLE')}
          submitLoading={isLoadingAction}
          disabled={!canApproveClient}
          content={
            <Trans
              i18nKey={`PENDING_CUSTOMERS:CONFIRMATION_MODAL:${selectedCustomer.action}`}
              components={{ s: <StrongModalText /> }}
              values={selectedCustomer}
            />
          }
        />
        <Snackbar
          open={isError}
          message={t(alertMessage?.description)}
          severity={'error'}
          handleClose={() => setIsError(false)}
        />
        <Snackbar
          open={isSuccess}
          message={t(alertMessage?.description)}
          severity='success'
          handleClose={() => setIsSuccess(false)}
        />
      </FullWidthContent>
    </MainMenuLayout>
  );
};

export default PendingCustomersScreen;
